import { BaseApiClient } from './base-client';
import { Product } from '../types/products';
import { ServiceInfo } from '../../types/service';
import { DashboardData } from '../types/products';

export class ProductsApiClient extends BaseApiClient {
  async getProducts(activeOnly: boolean = true): Promise<Product[]> {
    try {
      const response = await this.get<Product[]>('/api/v1/products/', {
        params: { is_active_only: activeOnly }
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  async getProduct(productId: number): Promise<Product> {
    return this.get<Product>(`/api/v1/products/${productId}`);
  }

  async getService(serviceId: number): Promise<ServiceInfo> {
    const response = await this.get<{ services: ServiceInfo[] }>('/api/v1/services/overview');
    const service = response.services.find(s => s.id === serviceId);
    if (!service) {
      throw new Error('Service not found');
    }
    return service;
  }

  async getDashboardData(): Promise<DashboardData> {
    return this.get<DashboardData>('/api/v1/dashboard/');
  }

  // Helper method to get default product option
  getDefaultOption(product: Product, optionType: string): number {
    const option = product.options.find(
      opt => opt.type === optionType && opt.default
    );
    return option ? option.value : 0;
  }

  // Helper method to get default period
  getDefaultPeriod(product: Product): number | undefined {
    const period = product.periods.find(p => p.default);
    return period?.id;
  }

  // Helper method to calculate base monthly price
  getBaseMonthlyPrice(product: Product): number {
    return product.price;
  }

  // Helper method to calculate option cost
  calculateOptionCost(product: Product, optionType: string, value: number): number {
    const option = product.options.find(opt => opt.type === optionType);
    if (!option) return 0;
    return option.price * value;
  }

  // Helper method to calculate period discount
  calculatePeriodDiscount(product: Product, periodId?: number): number {
    if (!periodId) return 0;
    const period = product.periods.find(p => p.id === periodId);
    return period ? period.discount : 0;
  }

  // Helper method to check if product is active
  isProductActive(product: Product): boolean {
    return product.is_active;
  }

  // Helper method to check if product is recurring
  isProductRecurring(product: Product): boolean {
    return product.is_recuring;
  }

  // Helper method to get formatted product name with specs
  getProductDisplayName(product: Product): string {
    const specs = [];
    if (product.disk_space) {
      specs.push(`${product.disk_space}GB Storage`);
    }
    if (product.allowed_users) {
      specs.push(`${product.allowed_users} Users`);
    }
    return specs.length > 0 ? `${product.name} (${specs.join(', ')})` : product.name;
  }

  // Get configuration options for a product
  async getProductConfig(productId: number): Promise<any> {
    return this.get<any>(`/api/v1/products/${productId}/config`);
  }

  // Update service settings
  async updateService(serviceId: number, data: any): Promise<ServiceInfo> {
    return this.put<ServiceInfo>(`/api/v1/services/${serviceId}`, data);
  }
}
